import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useMutation, useQuery } from "@apollo/client";
import { useLocalization } from "../../contexts/LocalizationContext";
import { useAuthUser } from "../../contexts/AuthUserContext";
import { useShop } from "../../contexts/ShopContext";
import {
  GET_NOTIFICATION_SETTINGS,
  GET_SUBSCRIPTION_STATUS,
} from "../../graphql/queries";
import {
  DELETE_USER,
  TURN_OFF_NOTIFICATIONS,
  TURN_ON_NOTIFICATIONS,
} from "../../graphql/mutations";
import { summarizeSubscription } from "../../utils/subscription";
import pageStyles from "../../styles/PageLayout.module.css";
import styles from "./SettingsPage.module.css";
import { fetchCurrentUser, sendInvite } from "../../utils/api/api";

const SettingsPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, signOut, setUser } = useAuthUser();
  const { locale, setLocale, t } = useLocalization();
  const { shopData } = useShop();
  const authToken = user?.token;
  const headers = authToken ? { Authorization: `Bearer ${authToken}` } : {};
  const [expandedId, setExpandedId] = useState(null);
  const [notificationsEnabled, setNotificationsEnabled] = useState(false);
  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteStatus, setInviteStatus] = useState("idle");
  const [inviteMessage, setInviteMessage] = useState("");
  const [sendingInvite, setSendingInvite] = useState(false);

  useEffect(() => {
    const fetchAdmin = async () => {
      if (!authToken) return;
      if (user?.isAdmin !== undefined) return;
      try {
        const data = await fetchCurrentUser(authToken);
        setUser((prev) => ({ ...(prev || {}), isAdmin: data?.isAdmin }));
      } catch {
        // ignore — AuthUserContext handles 401/403
      }
    };

    fetchAdmin();
  }, [authToken, user, setUser]);

  const { data: subscriptionData, loading: subscriptionLoading, refetch: _refetchSubscription } =
    useQuery(GET_SUBSCRIPTION_STATUS, {
      skip: !authToken,
      context: { headers },
      fetchPolicy: "cache-and-network",
    });

  const subscriptionStatus = useMemo(
    () => subscriptionData?.getSubscriptionStatus,
    [subscriptionData]
  );
  const subscriptionSummary = useMemo(
    () => summarizeSubscription(subscriptionStatus),
    [subscriptionStatus],
  );
  const subscriptionTypeLabel = useMemo(() => {
    const pf = subscriptionSummary.planFamily;
    if (pf === "STARTER") return "Starter Plan";
    if (pf === "PRO") return "Pro Plan";
    if (pf === "PRO_PLUS") return "Pro Plus Plan";
    if (subscriptionSummary.isMonthlySubscriptionRecord) return "Monthly Subscription";
    return subscriptionSummary.type || t("SettingsScreen.unknown");
  }, [subscriptionSummary, t]);

  const totalCredits = subscriptionSummary.totalCredits ?? subscriptionSummary.creditTier ?? null;
  const creditsUsed  = subscriptionSummary.creditsUsed  ?? 0;
  const creditsLeft  = totalCredits !== null ? Math.max(0, totalCredits - creditsUsed) : null;
  const creditPct    = totalCredits ? Math.min(100, Math.round((creditsUsed / totalCredits) * 100)) : 0;

  const CREDIT_ACTIONS = [
    // { label: "Chat Messages / Task Executions", cost: 1 },
    { label: "Image Generation", cost: 1 },
    { label: "Social Posts", cost: 1 },
    { label: "Campaign Analysis", cost: 1 },
    { label: "Ad Set Analysis", cost: 1 },
    { label: "Ad Analysis", cost: 1 },
    { label: "Blog Repair", cost: 1 },
    { label: "PDP Repair", cost: 1 },
    // { label: "Video Generation*",               cost: 12 },
    // { label: "TikTok Slideshows",               cost: 3 },
    // { label: "Email Campaigns",                 cost: 3 },
    { label: "Blog Articles", cost: 2 },
    // { label: "Ad Creatives", cost: 1 },
  ];

  const { data: notificationSettingsData, loading: notificationSettingsLoading, refetch: refetchNotificationSettings } =
    useQuery(GET_NOTIFICATION_SETTINGS, {
      skip: !authToken,
      context: { headers },
      fetchPolicy: "cache-and-network",
    });

  const [turnOnNotificationsMutation, { loading: turningOnNotifications }] =
    useMutation(TURN_ON_NOTIFICATIONS);
  const [turnOffNotificationsMutation, { loading: turningOffNotifications }] =
    useMutation(TURN_OFF_NOTIFICATIONS);
  const notificationsMutating = turningOnNotifications || turningOffNotifications;

  const [deleteUserMutation, { loading: deleting }] = useMutation(DELETE_USER, {
    context: { headers },
  });

  const shopName = useMemo(
    () => shopData?.shopOwnerName || shopData?.name || "Not connected",
    [shopData]
  );

  const toggle = (id) => setExpandedId((prev) => (prev === id ? null : id));

  const normalizeUrl = (raw) => {
    if (!raw) return null;
    const s = String(raw).trim();
    if (!s) return null;
    if (/^https?:\/\//i.test(s)) return s;
    return `https://${s}`;
  };

  const getShopUrl = (sd) =>
    normalizeUrl(sd?.primaryDomain?.url) ||
    (sd?.myshopifyDomain ? normalizeUrl(sd.myshopifyDomain) : null);

  const openShopSite = () => {
    const url = getShopUrl(shopData);
    if (!url) return;
    window.open(url, "_blank", "noopener");
  };

  useEffect(() => {
    const serverEnabled = notificationSettingsData?.myNotificationSettings?.receiveNotification;
    if (typeof serverEnabled === "boolean") {
      setNotificationsEnabled(serverEnabled);
    }
  }, [notificationSettingsData]);

  const toggleNotifications = async (value) => {
    if (!authToken || notificationsMutating || notificationSettingsLoading) return;
    const context = { headers };
    const previous = notificationsEnabled;
    setNotificationsEnabled(value);
    try {
      if (value) {
        await turnOnNotificationsMutation({ variables: { notificationToken: null }, context });
      } else {
        await turnOffNotificationsMutation({ context });
      }
      await refetchNotificationSettings?.();
    } catch {
      setNotificationsEnabled(previous);
      alert(t("SettingsScreen.notificationsErrorMessage"));
    }
  };

  const handleSignOut = async () => {
    try {
      window.sessionStorage.removeItem("zeqi:homeBootstrapped");
    } catch {
      // ignore
    }
    await signOut();
    navigate("/login", { replace: true });
  };

  const handleDeleteAccount = async () => {
    setShowDeleteConfirmModal(true);
  };

  const confirmDeleteAccount = async () => {
    try {
      await deleteUserMutation();
      try {
        window.sessionStorage.removeItem("zeqi:homeBootstrapped");
      } catch {
        // ignore
      }
      await signOut();
      navigate("/login", { replace: true });
    } catch {
      alert(
        t("SettingsScreen.deleteAccountFailed") ||
          "Unable to delete your account right now. Please try again.",
      );
    } finally {
      setShowDeleteConfirmModal(false);
    }
  };

  const handleSendInvite = async () => {
    if (!authToken) return;
    const email = String(inviteEmail || "").trim();
    if (!email) {
      setInviteStatus("error");
      setInviteMessage("Enter an email first.");
      return;
    }

    setSendingInvite(true);
    setInviteStatus("idle");
    setInviteMessage("");
    try {
      await sendInvite(email, authToken);
      setInviteStatus("success");
      setInviteMessage("Invite sent.");
      setInviteEmail("");
    } catch (err) {
      setInviteStatus("error");
      setInviteMessage(err?.message || "Unable to send invite.");
    } finally {
      setSendingInvite(false);
    }
  };

  const legalLinks = [
    {
      id: "legalTerms",
      label: t("SettingsScreen.terms"),
      desc: t("SettingsScreen.termsSummary"),
      url: "https://zeqi.ai/terms",
    },
    {
      id: "legalPrivacy",
      label: t("SettingsScreen.privacy"),
      desc: t("SettingsScreen.privacySummary"),
      url: "https://zeqi.ai/privacy",
    },
    {
      id: "legalDeleteData",
      label: t("SettingsScreen.deleteMyData"),
      desc: t("SettingsScreen.deleteDataSummary"),
      url: "https://zeqi.ai/delete-data",
    },
  ];

  const checkoutStatus = useMemo(() => {
    const value = new URLSearchParams(location.search).get("checkout");
    if (value === "success" || value === "cancel") return value;
    return null;
  }, [location.search]);

  const checkoutNotice =
    checkoutStatus === "success"
      ? t("SubscriptionPlanScreen.checkoutStartedTitle", {
          defaultValue: "Checkout opened",
        })
      : checkoutStatus === "cancel"
        ? t("SubscriptionPlanScreen.cancelConfirmTitle", {
            defaultValue: "Checkout cancelled",
          })
        : null;

  return (
    <section
      className={`${pageStyles.page} ${styles.wrapper}`}
      data-page="settings"
    >
      {checkoutNotice ? (
        <div className={styles.checkoutNotice} role="status" aria-live="polite">
          <span>{checkoutNotice}</span>
          <button
            type="button"
            className={styles.checkoutNoticeClose}
            onClick={() => navigate("/settings", { replace: true })}
          >
            Dismiss
          </button>
        </div>
      ) : null}

      {/* <header className={pageStyles.sectionHeader}>
        <h2>{t("SettingsScreen.account")}</h2>
        {/* <small>{t("SettingsScreen.profileFallbackEmail")}</small>
      </header> */}

      <div className={styles.profileCard}>
        <div className={styles.profilePill}>
          {(shopName || "ZT")
            .split(" ")
            .map((w) => w[0])
            .slice(0, 2)
            .join("")
            .toUpperCase()}
        </div>
        <div>
          <p className={styles.profileName}>{shopName}</p>
          <p className={styles.profileEmail}>{shopData?.email || user?.email || "-"}</p>
        </div>
      </div>

      <div className={styles.sectionBox}>
        <div className={styles.sectionHeader}>
          <h3>{t("SettingsScreen.account")}</h3>
        </div>

        <button className={styles.itemRow} onClick={() => toggle("accountSettings")}>
          <span>{t("SettingsScreen.accountSettings")}</span>
          <span>{expandedId === "accountSettings" ? "-" : "+"}</span>
        </button>
        {expandedId === "accountSettings" && (
          <div className={styles.content}>
            <div className={styles.rowBetween}>
              <span>{t("SettingsScreen.language")}</span>
              <div className={styles.languageGroup}>
                {[
                  { code: "en", label: "EN" },
                  { code: "ar", label: "ع" },
                ].map(({ code, label }) => (
                  <button
                    key={code}
                    className={`${styles.langPill} ${
                      locale === code ? styles.langPillActive : ""
                    }`}
                    onClick={() => setLocale(code)}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        <button className={styles.itemRow} onClick={() => toggle("connectedShop")}>
          <span>{t("SettingsScreen.connectedShop")}</span>
          <span>{expandedId === "connectedShop" ? "-" : "+"}</span>
        </button>
        {expandedId === "connectedShop" && (
          <div className={styles.content}>
            {shopData?.name ? (
              <button className={styles.linkButton} onClick={openShopSite}>
                {shopData.name}
              </button>
            ) : (
              <p className={styles.soonText}>{t("SettingsScreen.soon")}</p>
            )}
          </div>
        )}

        <button
          className={styles.itemRow}
          onClick={() => toggle("notificationSettings")}
        >
          <span>{t("SettingsScreen.notifications")}</span>
          <span>{expandedId === "notificationSettings" ? "-" : "+"}</span>
        </button>
        {expandedId === "notificationSettings" && (
          <div className={styles.content}>
            <div className={styles.rowBetween}>
              <span>
                {notificationsEnabled
                  ? t("SettingsScreen.notificationsOn")
                  : t("SettingsScreen.notificationsOff")}
              </span>
              <label className={styles.toggle}>
                <input
                  type="checkbox"
                  checked={notificationsEnabled}
                  onChange={(event) => toggleNotifications(event.target.checked)}
                  disabled={!authToken || notificationsMutating || notificationSettingsLoading}
                />
                <span className={styles.toggleTrack} />
              </label>
            </div>
            <p className={styles.hintText}>{t("SettingsScreen.notificationsHint")}</p>
          </div>
        )}

        <button className={styles.itemRow} onClick={() => toggle("security")}>
          <span>{t("SettingsScreen.securityLogin")}</span>
          <span>{expandedId === "security" ? "-" : "+"}</span>
        </button>
        {expandedId === "security" && (
          <div className={styles.content}>
            <button className={styles.dangerButton} onClick={handleDeleteAccount} disabled={deleting}>
              {t("SettingsScreen.deleteAccount")}
            </button>
            <button className={styles.signOutButton} onClick={handleSignOut}>
              {t("SettingsScreen.signOut")}
            </button>
          </div>
        )}
      </div>

      {/* ── Credit Usage ─────────────────────────────────────────────── */}
      <div className={styles.sectionBox}>
        <div className={styles.sectionHeader}>
          <h3>Credit Usage</h3>
        </div>
        {subscriptionLoading ? (
          <p className={styles.soonText}>Loading...</p>
        ) : totalCredits === null ? (
          <p className={styles.soonText}>
            Subscribe to a plan to start tracking your credits.
          </p>
        ) : (
          <>
            <p className={styles.creditResetText}>
              Your current credit usage for this billing period.
              {subscriptionSummary.currentPeriodEnd
                ? ` Resets on ${new Date(subscriptionSummary.currentPeriodEnd).toLocaleDateString("en-US", { month: "numeric", day: "numeric", year: "numeric" })}.`
                : ""}
            </p>

            {/* Progress bar */}
            <div className={styles.creditBarBlock}>
              <div className={styles.creditBarLabelRow}>
                <span className={styles.creditBarLabel}>Credits Used</span>
                <span className={styles.creditBarLabel}>
                  {creditsUsed.toLocaleString()} / {totalCredits.toLocaleString()} ({creditPct}%)
                </span>
              </div>
              <div className={styles.creditBarTrack}>
                <div
                  className={styles.creditBarFill}
                  style={{ width: `${creditPct}%` }}
                />
              </div>
            </div>

            {/* Credits remaining */}
            <div className={styles.creditRemaining}>
              <span className={styles.creditRemainingNum}>{creditsLeft.toLocaleString()}</span>
              <span className={styles.creditRemainingLabel}>credits remaining</span>
            </div>

            {/* Usage breakdown */}
            <div className={styles.breakdownBlock}>
              <p className={styles.breakdownTitle}>Usage Breakdown</p>
              <div className={styles.breakdownTable}>
                <div className={`${styles.breakdownRow} ${styles.breakdownHead}`}>
                  <span>Action</span>
                  <span>Credits Each</span>
                  <span>Used</span>
                  <span>Total Credits</span>
                </div>
                {CREDIT_ACTIONS.map((action) => (
                  <div key={action.label} className={styles.breakdownRow}>
                    <span>{action.label}</span>
                    <span className={styles.breakdownMuted}>{action.cost}</span>
                    <span>0</span>
                    <span>0</span>
                  </div>
                ))}
              </div>
              {/* <p className={styles.breakdownNote}>*Videos longer than 12 seconds use 2x credits.</p> */}
            </div>
          </>
        )}
      </div>

      {/* ── Subscription ─────────────────────────────────────────────── */}
      <div className={styles.sectionBox}>
        <div className={styles.sectionHeader}>
          <h3>{t("SettingsScreen.billing")}</h3>
        </div>
        {subscriptionLoading ? (
          <p className={styles.soonText}>Loading subscription...</p>
        ) : (
          <div className={styles.subscriptionCard}>
            <div className={styles.subscriptionInfo}>
              <p className={styles.subscriptionPlanLabel}>
                You&apos;re currently on the <strong>{subscriptionTypeLabel}</strong>
              </p>
              <p className={`${styles.soonText} ${subscriptionSummary.isActive && !subscriptionSummary.isFree ? styles.subscriptionStatusActive : ""}`}>
                Status: <strong>{subscriptionSummary.status || "—"}</strong>
              </p>
              {subscriptionSummary.currentPeriodEnd ? (
                <p className={styles.soonText}>
                  Renews on:{" "}
                  {new Date(subscriptionSummary.currentPeriodEnd).toLocaleDateString("en-US", {
                    month: "numeric",
                    day: "numeric",
                    year: "numeric",
                  })}
                </p>
              ) : null}
              {subscriptionSummary.isPendingCheckout ? (
                <p className={styles.soonText}>
                  Checkout pending — open billing to complete.
                </p>
              ) : null}
            </div>
            <button
              className={styles.manageSubButton}
              onClick={() => navigate("/billing")}
            >
              Manage Subscription
            </button>
          </div>
        )}
      </div>

      <div className={styles.sectionBox}>
        <div className={styles.sectionHeader}>
          <h3>{t("SettingsScreen.legal")}</h3>
        </div>
        {legalLinks.map((item) => (
          <div key={item.id} className={styles.legalRow}>
            <div>
              <p className={styles.legalTitle}>{item.label}</p>
              <p className={styles.soonText}>{item.desc}</p>
            </div>
            <button
              className={styles.linkButton}
              onClick={() => window.open(item.url, "_blank", "noopener")}
            >
              {t("SettingsScreen.openLink")}
            </button>
          </div>
        ))}
      </div>

      {user?.isAdmin && (
        <div className={styles.sectionBox}>
          <div className={styles.sectionHeader}>
            <h3>Admin</h3>
          </div>
          <div className={styles.content}>
            <p className={styles.soonText}>Send an invite email.</p>
            <div className={styles.inviteRow}>
              <input
                className={styles.inviteInput}
                value={inviteEmail}
                onChange={(event) => setInviteEmail(event.target.value)}
                placeholder="email@company.com"
                type="email"
              />
              <button
                className={styles.inviteButton}
                onClick={handleSendInvite}
                disabled={sendingInvite}
              >
                {sendingInvite ? "Sending..." : "Send Invite"}
              </button>
            </div>
            {inviteMessage ? (
              <p
                className={
                  inviteStatus === "success"
                    ? styles.inviteSuccess
                    : styles.inviteError
                }
              >
                {inviteMessage}
              </p>
            ) : null}
          </div>
        </div>
      )}

      {/* <div className={styles.signOutCard}>
        <h3>{t("SettingsScreen.signOut")}</h3>
        <p className={styles.soonText}>
          {t("SettingsScreen.signOutHint") ||
            "Sign out of your account and return to the login page."}
        </p>
        <button className={styles.signOutButton} onClick={handleSignOut}>
          {t("SettingsScreen.signOut")}
        </button>
      </div> */}

      {showDeleteConfirmModal && (
        <div
          className={styles.deleteModalOverlay}
          role="presentation"
          onClick={() => !deleting && setShowDeleteConfirmModal(false)}
        >
          <div
            className={styles.deleteModalCard}
            role="dialog"
            aria-modal="true"
            aria-labelledby="delete-account-title"
            onClick={(event) => event.stopPropagation()}
          >
            <h3 id="delete-account-title" className={styles.deleteModalTitle}>
              {t("SettingsScreen.deleteAccountConfirmTitle") ||
                "Delete your account?"}
            </h3>
            <p className={styles.deleteModalText}>
              {t("SettingsScreen.deleteAccountConfirmBody") ||
                "This will permanently remove your Zeqi account and sign you out. This action cannot be undone."}
            </p>
            <div className={styles.deleteModalActions}>
              <button
                type="button"
                className={styles.deleteModalCancel}
                onClick={() => setShowDeleteConfirmModal(false)}
                disabled={deleting}
              >
                {t("common.cancel") || "Cancel"}
              </button>
              <button
                type="button"
                className={styles.deleteModalConfirm}
                onClick={confirmDeleteAccount}
                disabled={deleting}
              >
                {deleting
                  ? t("SettingsScreen.deletingAccount") || "Deleting..."
                  : t("SettingsScreen.deleteAccountConfirmCta") ||
                    "Delete account"}
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default SettingsPage;
